// cspell:disable
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import * as z from "zod";

const fedexCustomerRegistrationInputSchema_ = z.object({
  accountNumber: z.string(),
  customerName: z.string(),
  smartPostHubId: z.number().optional(),
  address: z.object({
    residential: z.boolean(),
    city: z.string(),
    countryCode: z.string(),
    postalCode: z.string(),
    streetLines: z.array(z.string()),
    stateOrProvinceCode: z.string(),
  }),
});
export const fedexCustomerRegistrationInputSchema: z.ZodType<FedexCustomerRegistrationInput> =
  fedexCustomerRegistrationInputSchema_;

const fedexCustomerRegistrationOutputSchema_ = z.object({
  childKey: z.string().nullish(),
  childSecret: z.string().nullish(),
  mfaOptions: z
    .array(
      z.object({
        accountAuthToken: z.string().nullish(),
        mfaRequired: z.boolean().nullish(),
        email: z.string().nullish(),
        phoneNumber: z.string().nullish(),
        options: z
          .object({
            invoice: z.string().nullish(),
            secureCode: z.array(z.string()).nullish(),
          })
          .nullish(),
      }),
    )
    .nullish(),
});
export const fedexCustomerRegistrationOutputSchema: z.ZodType<FedexCustomerRegistrationOutput> =
  fedexCustomerRegistrationOutputSchema_;

export interface FedexCustomerRegistrationOutput extends z.infer<
  typeof fedexCustomerRegistrationOutputSchema_
> {}

export interface FedexCustomerRegistrationInput extends z.infer<
  typeof fedexCustomerRegistrationInputSchema_
> {}

export const normalizeFedexAccountNumber = (accountNumber: string): string =>
  accountNumber.replace(/-/g, "").trim();

export const normalizeFedexInvoiceNumber = (invoiceNumber: string): string =>
  invoiceNumber.replace(/\D/g, "");

export const FEDEX_TEST_ACCOUNT_NUMBER = "700257037";
export const FEDEX_TEST_GROUND_ECONOMY_ACCOUNT_NUMBER = "740561073";
export const FEDEX_TEST_GROUND_ECONOMY_HUB_ID = "5531";

export const FEDEX_TEST_ACCOUNT_INFO: FedexCustomerRegistrationInput = {
  address: {
    residential: false,
    city: "NEW YORK",
    countryCode: RedoShopifyCountryCode.Us,
    postalCode: "100114624",
    streetLines: ["15 W 18TH ST FL 7"],
    stateOrProvinceCode: "NY",
  },
  accountNumber: FEDEX_TEST_ACCOUNT_NUMBER,
  customerName: "TEST MERCHANT",
};

// Test ground economy account
// 740561073 (40 FED EX PKWY FL 2, COLLIERVILLE, TN, 380178711, US. Hub id :5531)
export const FEDEX_TEST_INVOICE_DATA = {
  number: "234562278",
  currency: "USD",
  amount: "234.00",
  // (You can use any valid date in the YYYY-MM-DD format which is not older than 6 months)
  date: "2025-11-01",
};

export const TEST_FEDEX_PINS = [
  "234560",
  "234561",
  "234562",
  "234563",
  "234564",
  "234565",
  "234566",
  "234567",
  "234568",
  "234569",
];

export enum FedexMfaOptionType {
  INVOICE = "invoice",
  SECURE_CODE = "secureCode",
}

export enum FedexPinDeliveryOption {
  SMS = "SMS",
  CALL = "CALL",
  EMAIL = "EMAIL",
}

// RPC Input/Output Types
const createDirectFedexCarrierAccountInputSchema_ = z.object({
  accountInfo: fedexCustomerRegistrationInputSchema_,
  merchantFacingName: z.string(),
});
export const createDirectFedexCarrierAccountInputSchema: z.ZodType<CreateDirectFedexCarrierAccountInput> =
  createDirectFedexCarrierAccountInputSchema_;

export interface CreateDirectFedexCarrierAccountInput extends z.infer<
  typeof createDirectFedexCarrierAccountInputSchema_
> {}

const createDirectFedexCarrierAccountOutputSchema_ = z.object({
  integrationId: z.string().optional(),
  mfaRequired: z.boolean(),
  mfaOptions: z
    .object({
      accountAuthToken: z.string(),
      hasInvoiceOption: z.boolean(),
      pinDeliveryOptions: z.array(z.string()),
    })
    .optional(),
  error: z.string().optional(),
});
export const createDirectFedexCarrierAccountOutputSchema: z.ZodType<CreateDirectFedexCarrierAccountOutput> =
  createDirectFedexCarrierAccountOutputSchema_;

export interface CreateDirectFedexCarrierAccountOutput extends z.infer<
  typeof createDirectFedexCarrierAccountOutputSchema_
> {}

// Base schema for common fields
const submitFedexMfaDataBaseSchema = z.object({ integrationId: z.string() });

// Invoice validation schema
const submitFedexMfaDataInvoiceSchema = submitFedexMfaDataBaseSchema.extend({
  mfaType: z.literal(FedexMfaOptionType.INVOICE),
  invoiceNumber: z.string(),
  invoiceCurrency: z.string(),
  invoiceAmount: z.string(),
  invoiceDate: z.string(),
});

export type SubmitFedexMfaDataInvoiceInput = z.infer<
  typeof submitFedexMfaDataInvoiceSchema
>;

// PIN validation schema
const submitFedexMfaDataPinSchema = submitFedexMfaDataBaseSchema.extend({
  mfaType: z.literal(FedexMfaOptionType.SECURE_CODE),
  pin: z.string(),
});

export type SubmitFedexMfaDataPinInput = z.infer<
  typeof submitFedexMfaDataPinSchema
>;

// Discriminated union for input
const submitFedexMfaDataInputSchema_ = z.discriminatedUnion("mfaType", [
  submitFedexMfaDataInvoiceSchema,
  submitFedexMfaDataPinSchema,
]);
export const submitFedexMfaDataInputSchema: z.ZodType<SubmitFedexMfaDataInput> =
  submitFedexMfaDataInputSchema_;
export type SubmitFedexMfaDataInput = z.infer<
  typeof submitFedexMfaDataInputSchema_
>;

const submitFedexMfaDataOutputSchema_ = z.object({
  success: z.boolean(),
  error: z.string().optional(),
});
export const submitFedexMfaDataOutputSchema: z.ZodType<SubmitFedexMfaDataOutput> =
  submitFedexMfaDataOutputSchema_;

export interface SubmitFedexMfaDataOutput extends z.infer<
  typeof submitFedexMfaDataOutputSchema_
> {}

// PIN Generation schemas
const generateFedexPinInputSchema_ = z.object({
  integrationId: z.string(),
  deliveryOption: z.enum(["SMS", "CALL", "EMAIL"]),
});
export const generateFedexPinInputSchema: z.ZodType<GenerateFedexPinInput> =
  generateFedexPinInputSchema_;

export interface GenerateFedexPinInput extends z.infer<
  typeof generateFedexPinInputSchema_
> {}

const generateFedexPinOutputSchema_ = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  message: z.string().optional(),
});
export const generateFedexPinOutputSchema: z.ZodType<GenerateFedexPinOutput> =
  generateFedexPinOutputSchema_;

export interface GenerateFedexPinOutput extends z.infer<
  typeof generateFedexPinOutputSchema_
> {}

// Backup MFA PIN schemas
const submitFedexMfaBackupInputSchema_ = z.object({
  integrationId: z.string(),
  pin: z.string(),
});
export const submitFedexMfaBackupInputSchema: z.ZodType<SubmitFedexMfaBackupInput> =
  submitFedexMfaBackupInputSchema_;

export interface SubmitFedexMfaBackupInput extends z.infer<
  typeof submitFedexMfaBackupInputSchema_
> {}

const submitFedexMfaBackupOutputSchema_ = z.object({
  success: z.boolean(),
  error: z.string().optional(),
});
export const submitFedexMfaBackupOutputSchema: z.ZodType<SubmitFedexMfaBackupOutput> =
  submitFedexMfaBackupOutputSchema_;

export interface SubmitFedexMfaBackupOutput extends z.infer<
  typeof submitFedexMfaBackupOutputSchema_
> {}

export interface SmartPostHub {
  name: string;
  acronym: string;
  hubId: number;
}

// https://developer.fedex.com/api/en-ag/guides/api-reference.html#ground%C2%AEeconomyhubids
export const smartPostHubs: SmartPostHub[] = [
  { name: "Northborough", acronym: "NOMA", hubId: 5015 },
  { name: "Windsor", acronym: "WICT", hubId: 5061 },
  { name: "Saks Fifth Avenue", acronym: "SAKS", hubId: 5064 },
  { name: "Edison", acronym: "EDNJ", hubId: 5087 },
  { name: "Newark", acronym: "NENJ", hubId: 5095 },
  { name: "South Brunswick", acronym: "SBNJ", hubId: 5097 },
  { name: "Newburgho", acronym: "NENY", hubId: 5110 },
  { name: "Pittsburgh", acronym: "PTPA", hubId: 5150 },
  { name: "Macungie", acronym: "MAPA", hubId: 5183 },
  { name: "Allentown", acronym: "ALPA", hubId: 5185 },
  { name: "Scranton", acronym: "SCPA", hubId: 5186 },
  { name: "Philadelphia", acronym: "PHPA", hubId: 5194 },
  { name: "Baltimore", acronym: "BAMD", hubId: 5213 },
  { name: "Martinsburg", acronym: "MAWV", hubId: 5254 },
  { name: "Charlotte", acronym: "CHNC", hubId: 5281 },
  { name: "Atlanta", acronym: "ATGA", hubId: 5303 },
  { name: "Orlando", acronym: "ORFL", hubId: 5327 },
  { name: "Tampa", acronym: "TAFL", hubId: 5345 },
  { name: "Memphis", acronym: "METN", hubId: 5379 },
  { name: "Grove City", acronym: "GCOH", hubId: 5431 },
  { name: "Groveport Ohio", acronym: "GPOH", hubId: 5436 },
  { name: "Indianapolis", acronym: "ININ", hubId: 5465 },
  { name: "Detroit", acronym: "DTMI", hubId: 5481 },
  { name: "New Berlin", acronym: "NBWI", hubId: 5531 },
  { name: "Minneapolis", acronym: "MPMN", hubId: 5552 },
  { name: "Wheeling", acronym: "WHIL", hubId: 5602 },
  { name: "St. Louis", acronym: "STMO", hubId: 5631 },
  { name: "Kansas City", acronym: "KCKS", hubId: 5648 },
  { name: "Dallas", acronym: "DLTX", hubId: 5751 },
  { name: "Houston", acronym: "HOTX", hubId: 5771 },
  { name: "Denver", acronym: "DNCO", hubId: 5802 },
  { name: "Salt Lake City", acronym: "SCUT", hubId: 5843 },
  { name: "Phoenix", acronym: "PHAZ", hubId: 5854 },
  { name: "Reno", acronym: "RENV", hubId: 5893 },
  { name: "Los Angeles", acronym: "LACA", hubId: 5902 },
  { name: "Chino", acronym: "COCA", hubId: 5929 },
  { name: "Sacramento", acronym: "SACA", hubId: 5958 },
  { name: "North Portland", acronym: "NPDX", hubId: 5972 },
  { name: "Seattle", acronym: "SEWA", hubId: 5983 },
];
